'use client';

import { useState, useEffect } from 'react';

interface ExtendedNavigator extends Navigator {
  standalone?: boolean;
}

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWADetection {
  isStandalone: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  isInstallable: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
}

export function usePWADetection(): PWADetection {
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Check if it's iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
    setIsIOS(isIOSDevice);

    // Check if it's Android
    const isAndroidDevice = /Android/.test(navigator.userAgent);
    setIsAndroid(isAndroidDevice);

    // Check if it's mobile (iOS or Android)
    const isMobileDevice = isIOSDevice || isAndroidDevice;
    setIsMobile(isMobileDevice);

    // Check if app is already installed (standalone mode)
    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as ExtendedNavigator).standalone === true ||
      window.matchMedia('(display-mode: fullscreen)').matches;

    setIsStandalone(isInStandaloneMode);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  return {
    isStandalone,
    isIOS,
    isAndroid,
    isMobile,
    isInstallable: !!deferredPrompt || isIOS || isAndroid,
    deferredPrompt,
  };
}
